import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Onboarding field validations (2026-05-22) — frontend mirror of backend
 * `@IsMinimumAge(13)` on `dateOfBirth`. The DOB control accepts an ISO
 * `YYYY-MM-DD` string (from `<input type="date">`) or any value `new Date()`
 * can parse.
 *
 * Rule:
 *   - DOB <= (today - N years) → ACCEPT (user is old enough, inclusive on
 *     the Nth birthday itself).
 *   - DOB > (today - N years)  → REJECT (under-age OR future date).
 *
 * Empty / null / undefined values are deferred to `Validators.required`.
 * Returns `{ minimumAge: { required, actualAgeYears } }` on rejection so
 * templates can render a precise inline message.
 */
export function minimumAgeValidator(minimumAge: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    if (typeof raw !== 'string') {
      return { minimumAge: { required: minimumAge, actualAgeYears: null } };
    }

    const dob = new Date(raw);
    if (Number.isNaN(dob.getTime())) {
      return { minimumAge: { required: minimumAge, actualAgeYears: null } };
    }

    // Compare in (Y, M, D) tuples to avoid local-vs-UTC mismatch on the
    // boundary day. `new Date('YYYY-MM-DD')` is parsed as UTC midnight,
    // while `today.getFullYear()/getMonth()/getDate()` are LOCAL.
    const today = new Date();
    const cutoffY = today.getFullYear() - minimumAge;
    const cutoffM = today.getMonth();
    const cutoffD = today.getDate();

    const dobY = dob.getUTCFullYear();
    const dobM = dob.getUTCMonth();
    const dobD = dob.getUTCDate();

    let acceptable: boolean;
    if (dobY !== cutoffY) acceptable = dobY < cutoffY;
    else if (dobM !== cutoffM) acceptable = dobM < cutoffM;
    else acceptable = dobD <= cutoffD;

    if (acceptable) return null;

    const actualAgeYears = computeAgeYears(dobY, dobM, dobD, today);
    return { minimumAge: { required: minimumAge, actualAgeYears } };
  };
}

/**
 * Whole-year age (today - DOB), floor to the most recent birthday.
 * Used to render the error message; not used in the cutoff comparison
 * (the (Y,M,D) tuple compare above is more robust on the boundary day).
 */
function computeAgeYears(
  dobY: number,
  dobM: number,
  dobD: number,
  today: Date,
): number {
  let age = today.getFullYear() - dobY;
  const beforeBirthday =
    today.getMonth() < dobM ||
    (today.getMonth() === dobM && today.getDate() < dobD);
  if (beforeBirthday) age -= 1;
  return age;
}

/**
 * Helper for templates: the latest acceptable DOB given `minimumAge`,
 * formatted as `YYYY-MM-DD`. Bind to `<input type="date" [max]="...">`
 * so the browser's native date-picker greys out under-age dates.
 */
export function maxDobIso(minimumAge: number): string {
  const today = new Date();
  const d = new Date(
    today.getFullYear() - minimumAge,
    today.getMonth(),
    today.getDate(),
  );
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
